import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Wallet, Clock, CheckCircle2 } from "lucide-react";

const brl = (n) => (Number(n || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("pt-BR") : "—";

const STATUS_LABEL = { aberto: "PAGAMENTO EM ABERTO", andamento: "EM ANDAMENTO", pago: "PAGO" };
const STATUS_STYLE = {
  aberto: "bg-amber-100 text-amber-800 border-amber-200",
  andamento: "bg-sky-100 text-sky-800 border-sky-200",
  pago: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const STATUS_DOT = { aberto: "🟠", andamento: "🔵", pago: "🟢" };

export default function FinanceiroPanel() {
  const [data, setData] = useState({ atual: null, ultimo_pago: null, historico: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/financeiro/meu")
      .then(({ data }) => setData(data))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const atual = data.atual;
  const ultimo = data.ultimo_pago;

  if (loading) return <div className="text-slate-500 p-6">Carregando...</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><Wallet className="h-5 w-5 text-brand" /> Meu financeiro</CardTitle>
          <CardDescription>
            {atual ? (<>Período atual: <b>{fmtDate(atual.period_start)}</b> até <b>{fmtDate(atual.period_end)}</b></>)
                  : "Nenhum período cadastrado ainda. Aguarde o administrador."}
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid md:grid-cols-3 gap-4">
        <Card data-testid="fin-card-aberto" className="border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-amber-700 mb-3">
              <Wallet className="h-4 w-4" /> Valor em aberto
            </div>
            <div className="text-4xl font-heading font-bold text-slate-900">{brl(atual?.valor_aberto)}</div>
          </CardContent>
        </Card>
        <Card data-testid="fin-card-andamento" className="border-sky-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-sky-700 mb-3">
              <Clock className="h-4 w-4" /> Em andamento
            </div>
            <div className="text-4xl font-heading font-bold text-slate-900">{brl(atual?.valor_andamento)}</div>
          </CardContent>
        </Card>
        <Card data-testid="fin-card-pago" className="border-emerald-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-emerald-700 mb-3">
              <CheckCircle2 className="h-4 w-4" /> Último pagamento
            </div>
            <div className="text-4xl font-heading font-bold text-slate-900">{brl(ultimo?.valor_pago)}</div>
            <div className="text-xs text-slate-500 mt-2">
              {ultimo ? (<>Período: {fmtDate(ultimo.period_start)} a {fmtDate(ultimo.period_end)}</>) : "Nenhum pagamento ainda."}
            </div>
          </CardContent>
        </Card>
      </div>

      {atual && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-wide text-slate-500">Status:</span>
              <Badge variant="outline" className={`${STATUS_STYLE[atual.status]} text-sm px-3 py-1`}>
                {STATUS_DOT[atual.status]} {STATUS_LABEL[atual.status]}
              </Badge>
            </div>
            {atual.observacao && (
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Observação do administrador</div>
                <div className="rounded-lg border-l-4 border-brand bg-slate-50 p-4 text-slate-700 whitespace-pre-wrap" data-testid="fin-observacao">
                  {atual.observacao}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Histórico</CardTitle>
          <CardDescription>{data.historico.length} período(s) registrado(s).</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Período</TableHead>
                <TableHead className="text-right">Em aberto</TableHead>
                <TableHead className="text-right">Em andamento</TableHead>
                <TableHead className="text-right">Pago</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Pagamento</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.historico.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">Sem registros.</TableCell></TableRow>}
                {data.historico.map((h, i) => (
                  <TableRow key={h.id} className="row-fade" style={{ animationDelay: `${i * 20}ms` }}>
                    <TableCell>{fmtDate(h.period_start)} - {fmtDate(h.period_end)}</TableCell>
                    <TableCell className="text-right font-mono">{brl(h.valor_aberto)}</TableCell>
                    <TableCell className="text-right font-mono">{brl(h.valor_andamento)}</TableCell>
                    <TableCell className="text-right font-mono">{brl(h.valor_pago)}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_STYLE[h.status]}>{STATUS_LABEL[h.status]}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-600">{fmtDate(h.data_pagamento)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
